import React, {useEffect, useState} from 'react';
import {Check, X, ChevronUp, ChevronDown} from 'lucide-react';
import {YourCartProps} from "../../@types/cart";
import ButtonPrimary from "../Form/Button/ButtoPrimary";

const YourBottomCart: React.FC<YourCartProps> = ({ cart, onSignup, onChangeAddress, buttonText, isLoading, hideButton, onValidated }) => {

  const [isVisible, setIsVisible] = useState<boolean>(false);

  const payMonthly = cart?.products?.filter((product) => product.isMonthly)?.sort((a, b) => {
    if (a.type === 'PLAN' && b.type !== 'PLAN') return -1;
    if (a.type !== 'PLAN' && b.type === 'PLAN') return 1;
    return 0;
  });
  const payToday = cart?.products?.filter((product) => !product.isMonthly && (product.cost > 0 || product.type === 'PROMO'))?.sort((a, b) => {
    if (a.type === 'ROUTER' && b.type === 'SHIPPING') return -1;
    if (a.type === 'SHIPPING' && b.type === 'ROUTER') return 1;
    return 0;
  });

  const hasPlan = cart?.products?.find((product) => product.type === 'PLAN') !== undefined;
  const hasRouter = cart?.products?.find((product) => product.type === 'ROUTER') !== undefined;
  const termedItem = cart?.products?.find((product) => product.type === 'ROUTER' && product.months > 0);

  // Separate promo items from regular products
  const promoItems = cart?.products?.filter(product => product.type === 'PROMO') || [];
  const regularMonthlyItems = payMonthly?.filter(product => product.type !== 'PROMO') || [];
  const regularOneOffItems = payToday?.filter(product => product.type !== 'PROMO') || [];
  
  // Calculate totals (promo items have negative costs, so they naturally reduce totals)
  const monthlyTotal = regularMonthlyItems?.reduce((sum, product) => sum + (product.cost / 100), 0) || 0;
  const oneOffTotal = regularOneOffItems?.reduce((sum, product) => sum + (product.cost / 100), 0) || 0;
  
  const monthlyDiscount = Math.abs(promoItems?.filter(p => p.isMonthly).reduce((sum, product) => sum + (product.cost / 100), 0) || 0);
  const oneOffDiscount = Math.abs(promoItems?.filter(p => !p.isMonthly).reduce((sum, product) => sum + (product.cost / 100), 0) || 0);
  
  // Final totals include promo discounts naturally
  const discountedMonthlyTotal = Math.max(0, payMonthly?.reduce((sum, product) => sum + (product.cost / 100), 0) || 0);
  const discountedOneOffTotal = Math.max(0, payToday?.reduce((sum, product) => sum + (product.cost / 100), 0) || 0);

  const toggleCart = () => {
    setIsVisible(!isVisible);
  };

  useEffect(() => {
    if (onValidated) {
      onValidated(hasPlan && hasRouter);
    }
  }, [hasPlan, hasRouter, onValidated]);

  return (
      <div className="w-full bg-white">
        <div className='px-4 bg-fred-blue-500 flex justify-between items-center'>
          <h3 className="text-xl text-left py-4 font-medium text-white flex-shrink-0">Your Cart</h3>
          <div className="flex-grow flex justify-center">

          </div>
          <div className="flex-shrink-0 flex justify-end" onClick={toggleCart}>
            {!isVisible ? (
              <ChevronUp className="w-10 h-10 text-white cursor-pointer" />
            ) : (
              <ChevronDown className="w-10 h-10 text-white cursor-pointer" />
            )}
          </div>
        </div>
        <div className={`${!isVisible ? 'hidden' : 'block'} mt-2`}>
          <div className="p-4 space-y-4 max-h-96 overflow-y-auto">
            <div className=''>
              <h5 className='mb-1 text-xl font-medium'>Your Address</h5>
              <p className='text-base'>{cart?.address?.fullAddress}</p>
              <p>
                <span className="font-bold underline cursor-pointer" onClick={() => onChangeAddress()}>CHANGE</span>
              </p>
            </div>
            <div>
              <h5 className='text-xl font-medium'>Products</h5>
              <div className="flex flex-col w-full space-y-1 mt-2">
                {!cart?.products && (
                    <p className='text-base font-normal'>No products selected</p>
                )}
                {cart?.products && cart.products.filter((product) => product.type === 'PLAN').map((product) => {
                  return (
                      <p key={product.productId}
                         className='text-base'>{`${product.productName}${termedItem ? ` (${termedItem.months}-month term)` : ''}`}</p>
                )
                })}
                {cart?.products && cart.products.filter((product) => product.type === 'ROUTER').map((product) => {
                  return (
                      <p key={product.productId} className='text-base'>{product.productName}</p>
                  )
                })}
              </div>
            </div>
            {(payMonthly && payMonthly.length > 0) && (
                <div>
                  <h5 className='text-xl font-medium mb-2'>Monthly Payment</h5>
                  {payMonthly.map((product) => {
                    return (
                        <div key={product.productId}>
                          <div className='flex items-center justify-between space-y-1'>
                            <p className={`text-base ${product.type === 'PROMO' ? 'text-green-600 font-semibold' : ''}`}>{product.productName}</p>
                            <p className={`text-base ${product.type === 'PROMO' ? 'text-green-600 font-semibold' : ''}`}>{product.type === 'PROMO' ? `-$${Math.abs(product.cost / 100)}` : `$${product.cost / 100}`}</p>
                          </div>
                        </div>
                    )
                  })}
                  <div className='flex justify-between items-center mt-2'>
                    <p className='text-base font-normal text-fred-purple-500'>Monthly Total</p>
                    {monthlyDiscount > 0 ? (
                      <p className='text-3xl font-semibold text-green-600'>${discountedMonthlyTotal}</p>
                    ) : (
                      <p className='text-3xl font-semibold text-fred-purple-500'>${discountedMonthlyTotal}</p>
                    )}
                  </div>
                </div>
            )}
            {(payToday && payToday.length > 0) && (
                <div>
                  <h5 className='text-xl font-medium mb-2'>One-off Payment</h5>
                  {payToday.map((product) => {
                    return (
                        <div key={product.productId} className='flex items-center justify-between space-y-1'>
                          <p className={`text-base ${product.type === 'PROMO' ? 'text-green-600 font-semibold' : ''}`}>{product.productName}</p>
                          <p className={`text-base ${product.type === 'PROMO' ? 'text-green-600 font-semibold' : ''}`}>{product.type === 'PROMO' ? `-$${Math.abs(product.cost / 100)}` : `$${product.cost / 100}`}</p>
                        </div>
                    )
                  })}
                  <div className='flex justify-between items-center mt-2'>
                    <p className='text-base font-normal text-fred-purple-500'>One-off Total</p>
                    {oneOffDiscount > 0 ? (
                      <p className='text-3xl font-semibold text-green-600'>${discountedOneOffTotal}</p>
                    ) : (
                      <p className='text-3xl font-semibold text-fred-purple-500'>${discountedOneOffTotal}</p>
                    )}
                  </div>
                </div>
            )}
          </div>
          <div className='px-4'>
            <p className="text-sm italic">All prices are inclusive of GST</p>
          </div>
          <div className='p-4 flex justify-between gap-x-4'>
            <div
                className={`w-1/2 flex items-center p-2 text-sm rounded-lg ${hasPlan ? 'text-green-800 bg-green-50' : 'text-red-800 bg-red-50'}`}
                role="alert">
              {hasPlan ? (
                <Check className="flex-shrink-0 inline w-5 h-5 me-2" />
              ) : (
                <X className="flex-shrink-0 inline w-5 h-5 me-2" />
              )}
              <div>
                <span className="font-medium">Base plan</span>
              </div>
            </div>
            <div
                className={`w-1/2 flex items-center p-2 text-sm rounded-lg ${hasRouter ? 'text-green-800 bg-green-50' : 'text-red-800 bg-red-50'}`}
                role="alert">
              {hasRouter ? (
                <Check className="flex-shrink-0 inline w-5 h-5 me-2" />
              ) : (
                <X className="flex-shrink-0 inline w-5 h-5 me-2" />
              )}
              <div>
                <span className="font-medium">Router</span>
              </div>
            </div>
          </div>
          {!hideButton && (
              <div className='p-4 flex justify-center'>
                <ButtonPrimary text={buttonText ? buttonText : 'Sign Up Now'}
                               disabled={!hasPlan || !hasRouter || isLoading} onClick={() => onSignup?.()}/>
              </div>
          )}
        </div>
      </div>
  );
};

export default YourBottomCart;
